import React, { useState, useEffect } from 'react';
import { X, Search, UserPlus } from 'lucide-react';
import { contactApi } from '../lib/api';
import type { Tables } from '../lib/types';

interface NewAgenziaAmoreClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (client: {
    name: string;
    birthdate: string;
    gender: string;
    city: string;
    email: string;
    phone: string;
    status: 'active' | 'inactive' | 'matched';
    preferences: string;
    notes?: string;
    contact_id?: string;
  }) => void;
}

const NewAgenziaAmoreClientModal: React.FC<NewAgenziaAmoreClientModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [mode, setMode] = useState<'select' | 'new'>('select');
  const [contacts, setContacts] = useState<Tables<'contacts'>[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Tables<'contacts'> | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    birthdate: '',
    gender: 'uomo_etero',
    city: '',
    email: '',
    phone: '',
    status: 'active' as 'active' | 'inactive' | 'matched',
    preferences: '',
    notes: '',
  });

  useEffect(() => {
    if (isOpen && mode === 'select') {
      loadContacts();
    }
  }, [isOpen, mode]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const data = await contactApi.getAll();
      setContacts(data);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthdate: string) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({
      name: '',
      birthdate: '',
      gender: 'uomo_etero',
      city: '',
      email: '',
      phone: '',
      status: 'active',
      preferences: '',
      notes: '',
    });
    setMode('select');
    setSearchTerm('');
    setSelectedContact(null);
    onClose();
  };

  const handleSelectContact = (contact: Tables<'contacts'>) => {
    onSubmit({
      name: contact.name,
      birthdate: contact.birthdate || '',
      gender: contact.gender,
      city: contact.city || '',
      email: contact.email || '',
      phone: contact.phone || '',
      status: 'active',
      preferences: '',
      notes: contact.notes || '',
      contact_id: contact.id
    });

    setMode('select');
    setSearchTerm('');
    setSelectedContact(null);
    onClose();
  };

  const handleClose = () => {
    setMode('select');
    setSearchTerm('');
    setSelectedContact(null);
    onClose();
  };

  const getGenderLabel = (gender: string) => {
    switch (gender) {
      case 'uomo_etero':
        return 'Uomo Etero';
      case 'donna_etero':
        return 'Donna Etero';
      case 'uomo_gay':
        return 'Uomo Gay';
      case 'donna_gay':
        return 'Donna Gay';
      default:
        return 'Non Dichiarato';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Nuovo Cliente Agenzia Amore</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setMode('select')}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                mode === 'select'
                  ? 'bg-pink-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Search className="h-5 w-5" />
              Seleziona Cliente Esistente
            </button>
            <button
              type="button"
              onClick={() => setMode('new')}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                mode === 'new'
                  ? 'bg-pink-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserPlus className="h-5 w-5" />
              Crea Nuovo Cliente
            </button>
          </div>

          {mode === 'select' ? (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Cerca per nome, email o telefono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-500">Caricamento...</div>
              ) : filteredContacts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? 'Nessun cliente trovato' : 'Nessun cliente disponibile'}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                  {filteredContacts.map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => handleSelectContact(contact)}
                      className="w-full px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-pink-50 transition-colors text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{contact.name}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {contact.email} • {contact.phone}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {getGenderLabel(contact.gender)} • {contact.city}
                            {contact.birthdate && ` • ${calculateAge(contact.birthdate)} anni`}
                          </div>
                        </div>
                        <div className="text-pink-600">
                          <UserPlus className="h-5 w-5" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Completo *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              placeholder="Inserisci il nome completo"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data di Nascita *
              </label>
              <input
                type="date"
                required
                value={formData.birthdate}
                onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Orientamento Sessuale *
              </label>
              <select
                required
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="uomo_etero">Uomo Etero</option>
                <option value="donna_etero">Donna Etero</option>
                <option value="uomo_gay">Uomo Gay</option>
                <option value="donna_gay">Donna Gay</option>
                <option value="non_dichiarato">Non Dichiarato</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Città *
            </label>
            <input
              type="text"
              required
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              placeholder="Città di residenza"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="email@esempio.it"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefono *
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="+39 123 456 7890"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stato
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' | 'matched' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            >
              <option value="active">Attivo</option>
              <option value="inactive">Inattivo</option>
              <option value="matched">Abbinato</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferenze Partner
            </label>
            <textarea
              value={formData.preferences}
              onChange={(e) => setFormData({ ...formData, preferences: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              placeholder="Descrivi le preferenze del cliente per il partner ideale..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              placeholder="Aggiungi note aggiuntive..."
            />
          </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors font-medium"
                >
                  Crea Cliente
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewAgenziaAmoreClientModal;
